import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { UserMinus, ArrowLeft, Building2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import MonthPicker, { currentMonthYm, humanMonth } from "@/components/MonthPicker";

const REHIRE_COLOR = {
  Yes: "text-emerald-600 font-semibold",
  No: "text-red-600 font-semibold",
  Conditional: "text-amber-600 font-semibold",
  Pending: "text-slate-500",
};

export default function DashboardLeft() {
  const [params] = useSearchParams();
  const month = params.get("month") || currentMonthYm();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/dashboard/left?month=${month}`);
        if (live) setData(data);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [month]);

  return (
    <div className="space-y-5" data-testid="dashboard-left-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/" className="text-slate-400 hover:text-slate-700" data-testid="back-to-dashboard">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <UserMinus className="h-7 w-7 text-red-600" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">HR Snapshot</div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
              Left · {humanMonth(month)}
            </h1>
          </div>
        </div>
        <MonthPicker testId="left-month" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-3" data-testid="left-count-strip">
        <div className="text-3xl font-bold text-red-600" style={{ fontFamily: "Chivo" }}>
          {loading ? "…" : (data?.count ?? 0)}
        </div>
        <div className="text-sm text-slate-600">
          employees with LWD between <span className="font-mono">{data?.month_first}</span> and{" "}
          <span className="font-mono">{data?.month_last}</span>
        </div>
      </div>

      {!loading && (data?.count ?? 0) === 0 && (
        <div className="border border-dashed border-slate-300 rounded-lg p-12 text-center text-sm text-slate-400">
          No employees left in {humanMonth(month)}.
        </div>
      )}

      {!loading && (data?.count ?? 0) > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-2">Employee</th>
                <th className="px-4 py-2">Punch ID / Code</th>
                <th className="px-4 py-2">Exit Type</th>
                <th className="px-4 py-2">Department</th>
                <th className="px-4 py-2">Unit</th>
                <th className="px-4 py-2">LWD</th>
                <th className="px-4 py-2">Notice Short</th>
                <th className="px-4 py-2">Recovery</th>
                <th className="px-4 py-2">Rehire</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((e) => (
                <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`left-row-${e.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/employees/${e.id}`} className="font-semibold text-slate-900 hover:text-red-600">
                      {e.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">
                    <div>{e.punch_id}</div>
                    <div className="text-slate-400">{e.employee_code}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className="bg-red-50 text-red-700 border border-red-200 text-[10px]">
                      {e.exit_type || "—"}
                    </Badge>
                    {e.exit_reason_category && (
                      <div className="text-[10px] text-slate-500 mt-1">{e.exit_reason_category}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700 text-xs">{e.department_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-700 text-xs">
                    <div className="flex items-center gap-1">
                      <Building2 className="h-3 w-3 text-slate-400" />
                      {e.unit_name || "—"}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{e.approved_lwd || "—"}</td>
                  <td className="px-4 py-3">
                    {(e.notice_shortfall_days || 0) > 0 ? (
                      <span className="text-red-600 font-semibold text-xs">{e.notice_shortfall_days}d</span>
                    ) : (
                      <span className="text-slate-400 text-xs">0</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-sm text-slate-900">
                    {(e.notice_recovery_estimate || 0) > 0 ? `₹${(e.notice_recovery_estimate).toLocaleString("en-IN")}` : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs ${REHIRE_COLOR[e.rehire_eligibility] || "text-slate-500"}`}>
                      {e.rehire_eligibility || "Pending"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
